import type { Document } from '@/types/document'

export type DocReviewGroupBy =
  | 'none'
  | 'vendor'
  | 'reference_number'
  | 'location'
  | 'source'
  | 'flags'

export const DOC_REVIEW_GROUP_OPTIONS: { value: DocReviewGroupBy; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'reference_number', label: 'Doc Ref #' },
  { value: 'location', label: 'Location' },
  { value: 'source', label: 'Source' },
  { value: 'flags', label: 'Flags' },
]

export const DOC_REVIEW_GROUP_CYCLE: DocReviewGroupBy[] = [
  'none',
  'vendor',
  'reference_number',
  'location',
  'source',
  'flags',
]

export const DOC_REVIEW_FLAG_GROUP_KEYS = [
  'duplicate',
  'duplicate_approved',
  'qbo_duplicate',
  'missing_categories',
  'missing_doc_type',
  'missing_location',
  'missing_unknown_vendor',
  'missing_bill_dates',
  'missing_line_items',
  'non_payable_notice',
  'total_mismatch',
  'shared_costs',
] as const

export type DocReviewFlagGroupKey = (typeof DOC_REVIEW_FLAG_GROUP_KEYS)[number]

export const DOC_REVIEW_FLAG_GROUP_LABELS: Record<DocReviewFlagGroupKey, string> = {
  duplicate: 'Duplicate in pending',
  duplicate_approved: 'Duplicate in approved',
  qbo_duplicate: 'Duplicate in QBO',
  missing_categories: 'Missing categories',
  missing_doc_type: 'Missing doc type',
  missing_location: 'Missing location',
  missing_unknown_vendor: 'Missing/unknown vendor',
  missing_bill_dates: 'Missing bill issue or due date',
  missing_line_items: 'No line items (bill or vendor credit)',
  non_payable_notice: 'Not an invoice / do not pay',
  total_mismatch: 'Total mismatch',
  shared_costs: 'Shared costs (tax, shipping, discount, tip, fees, …)',
}

export const DOC_REVIEW_NO_FLAGS_GROUP_KEY = '__no_flags__'
export const DOC_REVIEW_NO_FLAGS_GROUP_LABEL = '(No flags)'

export const DOC_REVIEW_SOURCE_GROUP_ORDER: Document['source_type'][] = [
  'email',
  'upload',
  'drive_scan',
  'amazon_report',
]

export const DOC_REVIEW_SOURCE_GROUP_LABELS: Record<Document['source_type'], string> = {
  email: 'Email',
  upload: 'Upload',
  drive_scan: 'Scan',
  amazon_report: 'Amazon',
}

export const DOC_REVIEW_BLANK_VENDOR_LABEL = '(No vendor)'
export const DOC_REVIEW_BLANK_REF_LABEL = '(No doc ref #)'
export const DOC_REVIEW_BLANK_LOCATION_LABEL = '(No location)'

export type DocReviewFlagContext = {
  pendingPeerDuplicateIds: Set<number>
  hasUnknownVendor: (doc: Document) => boolean
}

export function nextDocReviewGroupBy(current: DocReviewGroupBy): DocReviewGroupBy {
  const idx = DOC_REVIEW_GROUP_CYCLE.indexOf(current)
  const nextIdx = idx < 0 ? 0 : (idx + 1) % DOC_REVIEW_GROUP_CYCLE.length
  return DOC_REVIEW_GROUP_CYCLE[nextIdx]!
}

export function buildPendingPeerDuplicateIds(documents: Document[]): Set<number> {
  const normalizeVendor = (name: string | null | undefined): string => {
    if (!name) return ''
    const normalized = name
      .replace(/\s*\[[^\]]*\]\s*/g, ' ')
      .replace(/\s+(co\.|inc\.|llc|ltd\.|corp\.|corporation|company)\s*$/i, '')
      .replace(/\./g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
    return normalized
  }

  const groups = new Map<string, Document[]>()
  for (const doc of documents) {
    if (doc.reference_number && doc.status === 'pending_review') {
      const refKey = doc.reference_number.trim().toLowerCase()
      const key = `${normalizeVendor(doc.vendor_name)}:${refKey}`
      const bucket = groups.get(key) ?? []
      bucket.push(doc)
      groups.set(key, bucket)
    }
  }

  const pendingPeerDuplicateIds = new Set<number>()
  for (const docs of groups.values()) {
    if (docs.length > 1) {
      for (const doc of docs) {
        pendingPeerDuplicateIds.add(doc.id)
      }
    }
  }
  return pendingPeerDuplicateIds
}

function isDuplicatePeerForGrouping(
  doc: Document,
  pendingPeerDuplicateIds: Set<number>,
): boolean {
  return doc.duplicate_peer_pending === true || pendingPeerDuplicateIds.has(doc.id)
}

function isTotalMismatchReviewFlag(flag: string): boolean {
  return flag.startsWith('total_validation_mismatch:')
}

export function getDocReviewFlagGroupKeys(
  doc: Document,
  ctx: DocReviewFlagContext,
): DocReviewFlagGroupKey[] {
  const flags = doc.needs_review_flags ?? []
  const keys: DocReviewFlagGroupKey[] = []

  if (isDuplicatePeerForGrouping(doc, ctx.pendingPeerDuplicateIds)) {
    keys.push('duplicate')
  }
  if (doc.duplicate_matches_approved === true) {
    keys.push('duplicate_approved')
  }
  if (flags.some((f) => f === 'qbo_duplicate')) {
    keys.push('qbo_duplicate')
  }
  if (
    ctx.hasUnknownVendor(doc) ||
    flags.some((f) => f === 'missing_vendor')
  ) {
    keys.push('missing_unknown_vendor')
  }
  if (flags.some((f) => f === 'missing_doc_type')) {
    keys.push('missing_doc_type')
  }
  if (flags.some((f) => f === 'missing_location')) {
    keys.push('missing_location')
  }
  if (flags.some((f) => f === 'missing_categories')) {
    keys.push('missing_categories')
  }
  if (flags.some((f) => f === 'missing_bill_dates')) {
    keys.push('missing_bill_dates')
  }
  if (flags.some((f) => f === 'missing_line_items')) {
    keys.push('missing_line_items')
  }
  if (flags.some((f) => f === 'non_payable_notice')) {
    keys.push('non_payable_notice')
  }
  if (flags.some((f) => isTotalMismatchReviewFlag(f))) {
    keys.push('total_mismatch')
  }
  if (flags.some((f) => f === 'unallocated_shared_costs')) {
    keys.push('shared_costs')
  }

  return keys
}

export type DocReviewDocumentGroup = {
  key: string
  label: string
  documents: Document[]
}

function compareStringsBlankLast(a: string, b: string, blankLabels: string[]): number {
  const aBlank = blankLabels.includes(a)
  const bBlank = blankLabels.includes(b)
  if (aBlank && !bBlank) return 1
  if (!aBlank && bBlank) return -1
  return a.localeCompare(b, undefined, { sensitivity: 'base' })
}

export function vendorGroupLabel(doc: Document): string {
  const value = doc.vendor_name?.trim()
  return value ? value : DOC_REVIEW_BLANK_VENDOR_LABEL
}

export function referenceNumberGroupLabel(doc: Document): string {
  const value = doc.reference_number?.trim()
  return value ? value : DOC_REVIEW_BLANK_REF_LABEL
}

export function locationGroupLabel(doc: Document): string {
  const value = doc.location?.trim()
  return value ? value : DOC_REVIEW_BLANK_LOCATION_LABEL
}

export function sourceGroupLabel(doc: Document): string {
  return DOC_REVIEW_SOURCE_GROUP_LABELS[doc.source_type] ?? doc.source_type
}

export function sourceGroupSortIndex(sourceType: Document['source_type']): number {
  const idx = DOC_REVIEW_SOURCE_GROUP_ORDER.indexOf(sourceType)
  return idx < 0 ? DOC_REVIEW_SOURCE_GROUP_ORDER.length : idx
}

export function buildDocReviewGroups(
  documents: Document[],
  groupBy: DocReviewGroupBy,
  ctx: DocReviewFlagContext,
): DocReviewDocumentGroup[] {
  if (groupBy === 'none' || documents.length === 0) {
    return []
  }

  if (groupBy === 'flags') {
    const buckets = new Map<string, Document[]>()
    for (const doc of documents) {
      const flagKeys = getDocReviewFlagGroupKeys(doc, ctx)
      if (flagKeys.length === 0) {
        const bucket = buckets.get(DOC_REVIEW_NO_FLAGS_GROUP_KEY) ?? []
        bucket.push(doc)
        buckets.set(DOC_REVIEW_NO_FLAGS_GROUP_KEY, bucket)
        continue
      }
      for (const flagKey of flagKeys) {
        const bucket = buckets.get(flagKey) ?? []
        bucket.push(doc)
        buckets.set(flagKey, bucket)
      }
    }

    const orderedKeys: string[] = [
      ...DOC_REVIEW_FLAG_GROUP_KEYS.filter((key) => buckets.has(key)),
    ]
    if (buckets.has(DOC_REVIEW_NO_FLAGS_GROUP_KEY)) {
      orderedKeys.push(DOC_REVIEW_NO_FLAGS_GROUP_KEY)
    }

    return orderedKeys
      .map((key) => ({
        key,
        label:
          key === DOC_REVIEW_NO_FLAGS_GROUP_KEY
            ? DOC_REVIEW_NO_FLAGS_GROUP_LABEL
            : DOC_REVIEW_FLAG_GROUP_LABELS[key as DocReviewFlagGroupKey],
        documents: buckets.get(key) ?? [],
      }))
      .filter((group) => group.documents.length > 0)
  }

  const buckets = new Map<string, Document[]>()
  for (const doc of documents) {
    let key: string
    let label: string
    if (groupBy === 'vendor') {
      label = vendorGroupLabel(doc)
      key = label
    } else if (groupBy === 'reference_number') {
      label = referenceNumberGroupLabel(doc)
      key = label
    } else if (groupBy === 'location') {
      label = locationGroupLabel(doc)
      key = label
    } else {
      label = sourceGroupLabel(doc)
      key = doc.source_type
    }
    const bucket = buckets.get(key) ?? []
    bucket.push(doc)
    buckets.set(key, bucket)
  }

  const blankLabels =
    groupBy === 'vendor'
      ? [DOC_REVIEW_BLANK_VENDOR_LABEL]
      : groupBy === 'reference_number'
        ? [DOC_REVIEW_BLANK_REF_LABEL]
        : groupBy === 'location'
          ? [DOC_REVIEW_BLANK_LOCATION_LABEL]
          : []

  const entries = [...buckets.entries()].map(([key, groupDocs]) => {
    const label =
      groupBy === 'source'
        ? sourceGroupLabel(groupDocs[0]!)
        : key
    return { key, label, documents: groupDocs }
  })

  entries.sort((a, b) => {
    if (groupBy === 'source') {
      const aIdx = sourceGroupSortIndex(a.documents[0]!.source_type)
      const bIdx = sourceGroupSortIndex(b.documents[0]!.source_type)
      return aIdx - bIdx
    }
    return compareStringsBlankLast(a.label, b.label, blankLabels)
  })

  return entries.filter((group) => group.documents.length > 0)
}

export function docReviewCountOfTotal(
  hasActiveFilters: boolean,
  filteredTotal: number,
  unfilteredTotal: number | null,
): number {
  if (hasActiveFilters) {
    return unfilteredTotal ?? filteredTotal
  }
  return filteredTotal
}

export function docReviewCountFilteredSuffix(hasActiveFilters: boolean): string {
  return hasActiveFilters ? ' (filtered)' : ''
}

export function docReviewCountDocSuffix(total: number, hasActiveFilters: boolean): string {
  if (hasActiveFilters) return ''
  return ` doc${total !== 1 ? 's' : ''}`
}

export function docReviewPageRange(params: {
  isGrouped: boolean
  listPage: number
  pageSize: number
  filteredTotal: number
  documentCount: number
}): { start: number; end: number } {
  const { isGrouped, listPage, pageSize, filteredTotal, documentCount } = params
  if (filteredTotal === 0 && documentCount === 0) {
    return { start: 0, end: 0 }
  }
  if (isGrouped) {
    const end = documentCount > 0 ? documentCount : filteredTotal
    return { start: end === 0 ? 0 : 1, end }
  }
  return {
    start: (listPage - 1) * pageSize + 1,
    end: Math.min(listPage * pageSize, filteredTotal),
  }
}

/** Unique documents in grouped display order (first occurrence per id). */
export function uniqueDocsInDisplayOrder(
  documents: Document[],
  groups: DocReviewDocumentGroup[],
  groupBy: DocReviewGroupBy,
): Document[] {
  if (groupBy === 'none') {
    return documents
  }
  const seen = new Set<number>()
  const result: Document[] = []
  for (const group of groups) {
    for (const doc of group.documents) {
      if (seen.has(doc.id)) continue
      seen.add(doc.id)
      result.push(doc)
    }
  }
  return result
}
